import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Play, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";

export interface CaseItem {
  id: string;
  slug?: string;
  title: string;
  client: string;
  category: string;
  service: "Pulse" | "Prime";
  format: string;
  industry: string;
  metric?: { value: string; label: string };
  excerpt?: string;
}

export function CaseCard({ item, eager = false }: { item: CaseItem; eager?: boolean }) {
  const [active, setActive] = useState(false);
  const [hover, setHover] = useState(false);
  const ref = useRef<HTMLElement | null>(null);
  const thumb = `https://i.ytimg.com/vi/${item.id}/hqdefault.jpg`;

  const onMove = (e: React.MouseEvent<HTMLElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
  };

  return (
    <article
      ref={ref}
      onMouseMove={onMove}
      style={{
        backgroundImage:
          "radial-gradient(420px circle at var(--mx, 50%) var(--my, 50%), color-mix(in oklab, var(--mint) 14%, transparent), transparent 55%)",
      }}
      className="group relative overflow-hidden rounded-2xl bg-surface ring-1 ring-white/5 transition-[transform,box-shadow] duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_30px_60px_-25px_rgba(0,0,0,0.7)] hover:ring-mint/40"
      onMouseEnter={() => { setHover(true); track("case_hover_preview", { case_id: item.id }); }}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        onClick={() => setActive(true)}
        className="relative block aspect-video w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-mint/60"
        aria-label={`Spela ${item.title}`}
      >
        {active ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${item.id}?autoplay=1&rel=0&playsinline=1`}
            title={item.title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <>
            <img
              src={thumb}
              alt={`${item.client} — ${item.title}, ${item.format.toLowerCase()} producerad av Fenny`}
              loading={eager ? "eager" : "lazy"}
              decoding="async"
              className={cn(
                "h-full w-full object-cover transition-transform duration-[4000ms] ease-out will-change-transform",
                hover ? "scale-110" : "scale-100",
              )}
            />
            {hover && (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${item.id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${item.id}&modestbranding=1&playsinline=1&rel=0`}
                title=""
                aria-hidden
                tabIndex={-1}
                className="absolute inset-0 h-full w-full opacity-0 transition-opacity duration-700 [@media(hover:hover)]:opacity-100 pointer-events-none"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/10 to-transparent" />
            <span className="absolute right-3 top-3 rounded-full border border-white/20 bg-background/60 px-2.5 py-1 text-[11px] uppercase tracking-widest text-foreground/90 backdrop-blur">
              {item.service}
            </span>
            {item.metric && (
              <div className="absolute left-4 bottom-4 rounded-2xl border border-mint/30 bg-background/70 px-4 py-2 backdrop-blur">
                <div className="text-lg font-semibold leading-none text-mint">{item.metric.value}</div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-foreground/80">{item.metric.label}</div>
              </div>
            )}
            <div className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-foreground/95 text-background opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <Play className="h-5 w-5 translate-x-[1px] fill-current" />
            </div>
          </>
        )}
      </button>
      <div className="p-5">
        <div className="text-[11px] uppercase tracking-widest text-mint/80">{item.industry} · {item.category}</div>
        <h3 className="mt-2 text-base font-semibold leading-tight">
          {item.slug ? (
            <Link
              to="/case/$slug"
              params={{ slug: item.slug }}
              className="inline-flex items-start gap-1 transition-colors hover:text-mint"
              onClick={() => track("case_card_open", { case_id: item.id, slug: item.slug })}
            >
              {item.title}
              <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 opacity-60" />
            </Link>
          ) : (
            item.title
          )}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">{item.client}</p>
        {item.excerpt && <p className="mt-3 text-sm leading-relaxed text-foreground/80">{item.excerpt}</p>}
      </div>
    </article>
  );
}
